/* eslint-disable react/no-unstable-nested-components */
import { Button, Dialog, DialogContent, Table } from "@jhool-io/fe-components";
import { Link, useLocation, useParams } from "react-router-dom";
import { useMemo, useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import {
    IPrescription,
    IPrescriptionEvent,
    ISSOLaunchResponse,
} from "../../types/e-prescribe.types";
import {
    displayNameInRightFormat,
    formatDate,
} from "../../../../utils/helpers";
import { getPrecriptionStatusForDisplay } from "../../helpers/e-prescribe.helpers";
import ArrowUpRightIcon from "../../../../icons/ArrowUpRight";
import PrescriptionEvents from "../PrescriptionEvents/PrescriptionEvents";
import { useAppSelector } from "../../../../hooks/useRedux";
import { useTriggerSSOLaunch } from "../../hooks/e-prescribe.mutations";
import DrFirstIframe from "../DrFirstIframe/DrFirstIframe";
import useToast from "../../../../hooks/useToast";

interface PrescriptionListTableProps {
    data: IPrescription[];
    hideNoteData?: boolean;
    sessionHistoryId?: string;
}

export default function PrescriptionListTable({
    data,
    hideNoteData,
    sessionHistoryId,
}: PrescriptionListTableProps) {
    const [eventsInView, setEventsInView] = useState<
        IPrescriptionEvent[] | null
    >(null);
    const [launchData, setLaunchData] = useState<ISSOLaunchResponse | null>(
        null
    );

    const { practice } = useAppSelector((state) => state.userPractice);

    const location = useLocation();
    const params = useParams();
    const { clientId } = params;
    const isClientChart = location.pathname.includes("/clients");
    const isNoteSection = location.pathname.includes("/notes");

    const triggerSSOLaunch = useTriggerSSOLaunch();

    const { toast } = useToast();

    const columns: ColumnDef<IPrescription>[] = useMemo(() => {
        const handleTriggerSSOLaunch = async (prescriptionId: string) => {
            triggerSSOLaunch.mutate(
                {
                    practiceId: practice?.practice_id ?? "",
                    payload: {
                        landing_screen: "report",
                        session_history_id: sessionHistoryId,
                        client_id: clientId ?? undefined,
                        prescription_ids: [prescriptionId],
                        allow_login_without_patient_context: true,
                    },
                },

                {
                    onSuccess: (res) => {
                        setLaunchData(res.data);
                    },
                    onError: (err) => {
                        toast({
                            mode: "error",
                            message:
                                err?.response?.data?.message ??
                                "Cannot open prescription details at this time",
                        });
                    },
                }
            );
        };
        return [
            {
                accessorKey: "prescription_id",
                header: "",
                cell: ({ row }) => (
                    <div className="flex items-center gap-x-2">
                        <Button
                            variant="normal"
                            className="text-textLink text-[13px] ![border-bottom:1px_solid_var(--color-text-link)] flex items-center hover:text-text hover:![border-bottom:1px_solid_var(--color-text)] !rounded-none"
                            disabled={triggerSSOLaunch.isLoading}
                            type="button"
                            onClick={() =>
                                handleTriggerSSOLaunch(
                                    row.original.drfirst_prescription_id
                                )
                            }
                        >
                            View Details <ArrowUpRightIcon />
                        </Button>
                        <Button
                            variant="normal"
                            className="text-textLink text-[13px] ![border-bottom:1px_solid_var(--color-text-link)] flex items-center hover:text-text hover:![border-bottom:1px_solid_var(--color-text)] !rounded-none "
                            disabled={triggerSSOLaunch.isLoading}
                            type="button"
                            onClick={() =>
                                setEventsInView(
                                    row.original.prescription_events ?? []
                                )
                            }
                        >
                            View Events <ArrowUpRightIcon />
                        </Button>
                    </div>
                ),
            },
            {
                accessorKey: "client",
                header: "Client",
                cell: ({ row }) => (
                    <Link
                        to={`/clients/${row.original.client_id}?tab=prescriptions`}
                        className="underline text-textLink"
                    >
                        {displayNameInRightFormat({
                            firstName: row.original.client.first_name,
                            lastName: row.original.client.last_name,
                        })}
                    </Link>
                ),
            },
            {
                accessorKey: "provider",
                header: "Provider",
                cell: ({ row }) => (
                    <span>
                        {row.original.provider_id &&
                        row.original.provider &&
                        Object.keys(row.original.provider).length > 0
                            ? displayNameInRightFormat({
                                  firstName: row.original.provider.first_name,
                                  lastName: row.original.provider.last_name,
                              })
                            : "--"}
                    </span>
                ),
            },
            {
                accessorKey: "drug_description",
                header: "Drug Description",
                cell: ({ row }) => (
                    <div className="w-[300px] text-wrap">
                        {row.original.drug_description ?? "--"}
                    </div>
                ),
            },
            {
                accessorKey: "quantity",
                header: "Qty/ Unit",
                cell: ({ row }) => (
                    <span>{`${row.original.quantity ?? "--"}/${
                        row.original.quantity_unit ?? "--"
                    } `}</span>
                ),
            },
            {
                accessorKey: "status",
                header: "Status",
                cell: ({ row }) =>
                    row.original.status ? (
                        <span>
                            {getPrecriptionStatusForDisplay(
                                row.original.status
                            )}
                        </span>
                    ) : (
                        "--"
                    ),
            },
            {
                accessorKey: "refills",
                header: "Refills",
                cell: ({ row }) => row.original.refills ?? "--",
            },
            {
                accessorKey: "pharmacy_name",
                header: "Pharmacy Name",
                cell: ({ row }) => row.original.pharmacy_name ?? "--",
            },
            {
                accessorKey: "signed_date",
                header: "Signed Date",
                cell: ({ row }) =>
                    row.original.signed_date
                        ? formatDate(row.original.signed_date)
                        : "--",
            },
            {
                accessorKey: "completed_date",
                header: "Date Completed",
                cell: ({ row }) =>
                    row.original.completed_date
                        ? formatDate(row.original.completed_date)
                        : "--",
            },
            {
                accessorKey: "created_at",
                header: "Date Added",
                cell: ({ row }) =>
                    row.original.created_at
                        ? formatDate(row.original.created_at)
                        : "--",
            },
            {
                accessorKey: "drfirst_prescription_id",
                header: "",
                cell: ({ row }) =>
                    row.original.note_id ? (
                        <Link
                            to={`/notes/${row.original.client_id}/${row.original.note_id}?viewFrom=session_note&note_tab=prescriptions`}
                            className="text-textLink text-[13px] [border-bottom:1px_solid_var(--color-text-link)] flex items-center hover:text-text hover:[border-bottom:1px_solid_var(--color-text)]"
                        >
                            View Note <ArrowUpRightIcon />
                        </Link>
                    ) : (
                        "--"
                    ),
            },
        ];
    }, [
        clientId,
        practice?.practice_id,
        sessionHistoryId,
        toast,
        triggerSSOLaunch,
    ]);

    return (
        <>
            <DrFirstIframe
                launchData={launchData}
                onClose={() => setLaunchData(null)}
                title="View Prescription"
                clientId={clientId ?? ""}
                practiceId={practice?.practice_id ?? ""}
            />
            {eventsInView ? (
                <Dialog open={Boolean(eventsInView)}>
                    <DialogContent
                        title="Prescription Events"
                        handleCloseDialog={() => setEventsInView(null)}
                    >
                        <PrescriptionEvents events={eventsInView} />
                    </DialogContent>
                </Dialog>
            ) : null}
            <Table
                data={data}
                columns={columns}
                isColumnVisible={{
                    provider: !hideNoteData,
                    client: isClientChart ? false : !hideNoteData,
                    drfirst_prescription_id: !isNoteSection,
                }}
            />
        </>
    );
}
